import React, { useEffect, useMemo } from "react";
import { Modal, ModalBody } from "@vds/modals";
import { Title } from "@vds/typography";
import { Icon } from "@vds/icons";
import { FlexRow } from "../../components/core/utility";
import { Button } from "@vds/buttons";
import { css } from "@emotion/react";
import { DropdownSelect } from "@vds/selects";
import _ from "lodash";
import { MultiSelect } from "primereact/multiselect";
import styled from "styled-components";
import "./addmaterial.css";

const StyledModal = css`
  height: 458px;
  width: 434px;
  padding: 1rem 1rem 1rem 1rem;
  border-radius: 0.5rem;
  @media (max-width: 767px) {
    width: auto% !important;
    height: auto;
    min-height: 14.5rem;
    padding: 1rem 1rem 1rem 1rem;
    padding-right: 2px;
  }
  .ModalDialogWrapper-sc-ov12ij-0 > span.hULveg {
    padding-right: 0px;
  }

  & footer {
    padding: 0;
  }
`;

export const CustomisedMultiDropdown = styled(MultiSelect)`
  border: 1px solid #6f7171 !important;
  font-family: Verizon-NHG-eTX;
  width: 100%;
  box-shadow: none !important;
  border-radius: 4 !important;
  font-size: 16px !important;
  color: #000000 !important;
`;

export const CustomDropdown = styled(DropdownSelect)`
  & div {
    & span {
      & span {
        & div {
          border-radius: 4px !important;
          border: 1px solid #6f7171 !important;
        }
      }
    }
  }
`;

const AddMaterial = ({
  isOpen,
  onClose,
  recommendations = [],
  handleAddMaterial,
  addedMaterials,
  materialList = {}, //map { subscriptionId: [ {label, materialId, materialName, subscriptionId, subscriptionName} ] }
  existingHardwareMap = {}, // to filter requested hardware { subscriptionId: [materialId1, materialId2] }
  getContractDetailsApiToBeCalled = false,
  onChangeSubscriptionId,
}) => {
  const [selectedSubscriptionId, setSelectedSubscriptionId] = React.useState("");

  const [selectedMaterials, setSelectedMaterials] = React.useState(
    addedMaterials?.length
      ? addedMaterials.reduce((acc, item) => {
          if (!acc[item.subscriptionId]) acc[item.subscriptionId] = [];
          acc[item.subscriptionId].push(item.materialId);
          return acc;
        }, {})
      : {}
  ); //map { subscriptionId: [materialId1, materialId2] }
  const [materialsDropdown, setMaterialsDropdown] = React.useState([]);

  const subscriptionList = useMemo(() => {
    return _.uniqBy(
      recommendations?.map((item) => {
        return { id: item.subscriptionId, label: `${item.subscriptionName || item.name} - ${item.subscriptionId}`, value: item.subscriptionId };
      }),
      "id"
    );
  }, [recommendations]);

  const onClickAddMaterials = () => {
    const allSelectedMaterialList = Object.entries(selectedMaterials).flatMap(([subscriptionId, materialIds]) =>
      materialList[subscriptionId].filter((item) => materialIds.includes(item.materialId))
    );
    handleAddMaterial(allSelectedMaterialList);
    setSelectedMaterials({});
    setSelectedSubscriptionId("");
    onClose();
  };

  const onSubscriptionChange = (e) => {
    setSelectedSubscriptionId(e.target.value);
  };

  useEffect(() => {
    if (!selectedSubscriptionId) {
      setMaterialsDropdown([]);
      return;
    }

    let materials = materialList[selectedSubscriptionId] || [];
    const existingSelections = existingHardwareMap[Number(selectedSubscriptionId)] || [];

    if (existingSelections.length) {
      materials = materials.filter((item) => !existingSelections.includes(item.materialId));
    }

    setMaterialsDropdown(materials);
  }, [selectedSubscriptionId, materialList, existingHardwareMap]);

  return (
    <Modal
      role="dialog"
      ariaLabel="Configure MMIDs"
      hideCloseButton={false}
      opened={isOpen}
      disableOutsideClick
      css={StyledModal}
      closeButton={
        <FlexRow justifyContent="space-between" style={{ marginLeft: "8px" }} onClick={(event) => event.preventDefault()}>
          <Title>Add Material</Title>
          <div onClick={onClose} data-testId="close-icon">
            <Icon
              aria-label="close"
              alt="close-icon"
              role="img"
              name="close"
              size="medium"
              color="#000000"
              style={{ cursor: "pointer", outline: "none" }}
            />
          </div>
        </FlexRow>
      }
    >
      <ModalBody style={{ padding: "8px 8px 8px 4px" }}>
        <div style={{ flex: 1 }}>
          <CustomDropdown
            style={{ width: "100%" }}
            label="Select Subscription"
            value={selectedSubscriptionId || "Select Subscription"}
            onChange={(e) => {
              if (getContractDetailsApiToBeCalled) {
                onChangeSubscriptionId(e.target.value);
              }
              onSubscriptionChange(e);
            }}
          >
            <option disabled>Select Subscription</option>
            {subscriptionList.map((item) => {
              return (
                <option key={item.id} value={item.value} disabled={item.value === "Select"}>
                  {item.label}
                </option>
              );
            })}
          </CustomDropdown>
          <label
            htmlFor="material-multiselect"
            style={{
              fontSize: "12px",
              fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif",
              marginBottom: 4,
              display: "block",
              marginTop: "10px",
            }}
          >
            Select Material(s)
          </label>
          <CustomisedMultiDropdown
            inputId="material-multiselect"
            panelClassName="add-material-modal-multiselect"
            value={selectedMaterials[selectedSubscriptionId] || []}
            options={
              materialsDropdown.map((e) => {
                return { label: `${e.materialId} - ${e.materialName}`, ...e };
              }) || []
            }
            onChange={(e) =>
              setSelectedMaterials((prev) => {
                return { ...prev, [selectedSubscriptionId]: e.value };
              })
            }
            placeholder="Select Material"
            filter
            style={{ width: "100%" }}
            disabled={!selectedSubscriptionId}
            optionLabel="label"
            optionValue="materialId"
            maxSelectedLabels={0}
            filterPlaceholder="Search MMID"
            filterInputAutoFocus={false}
            filterBy="materialId"
            showSelectAll={false}
            emptyMessage="No materials available"
          />
        </div>
      </ModalBody>
      <div style={{ marginTop: "10px", position: "absolute", bottom: 0, right: 0 }}>
        <FlexRow justifyContent="flex-end">
          <Button size="small" onClick={onClickAddMaterials} disabled={!Object.keys(selectedMaterials).length || !selectedSubscriptionId}>
            Save
          </Button>
        </FlexRow>
      </div>
    </Modal>
  );
};
export default AddMaterial;
